import React from 'react';
import get from 'lodash.get';
import clsx from 'clsx';
import { Link } from 'acq-components/lib/base-components/Link/Link';
import { Sanitizer } from 'acq-components/lib/base-components/Sanitizer/Sanitizer';
import { Type, Color, Size, Align, Icon } from '@americanexpress/aqx-components';
import { createDtmHandler } from 'src/universal/utils/analyticsTracking';
import { createDtmAndTrackActionHandler } from 'src/universal/utils/analyticsTracking';
import { createCombinedApplyHandler } from 'src/universal/utils/applyOfferGatingHandler';
import LazyScrollMetricTracker from 'src/universal/components/DTM/LazyScrollMetricTracker';
import { suppModalState } from 'page_modules/business-pdp/utils/setModalStateAction';
import leftRailStyles from 'page_modules/business-pdp/components/StickyLeftRail/__styles__/index.scss?modules';
import addEmpStyles from 'page_modules/business-pdp/components/ApplyAndAddEmpCard/__styles__/index.scss?modules';
import tooltipStyles from 'src/universal/components/SuppModal/components/styles/toolTipWrapper.scss?modules';
import {
  getUpgradeCtaProps,
  getUpgradeEepForLink
} from 'src/universal/utils/orchestraUtils';
import { sendDTMClickEvent } from 'service_modules/models/dtm-tagging';
import { mapUrlWithDtmLinkQuery } from 'service_modules/models/dtm-tagging';
import replaceStringWithVars from 'src/universal/utils/replaceStringWithVars';
import { getDispatchFunc } from 'src/universal/utils/reduxStoreUtil';
import textRemoveTags from 'service_modules/utils/text-remove-tags';

const leftRailEventKey = 'LeftRail';

// ====== private methods ======

export const AddEmployeeCta = ({ cardInfo, links }) => {
  const toggleModal = () => {
    getDispatchFunc()(
      suppModalState(!cardInfo.suppModalOpen, leftRailEventKey.toLowerCase())
    );
  };

  const addAnEmployeeDtm = get(links, 'addAnEmployee.dtm') && {
    ...links.addAnEmployee.dtm,
    eventAsset: `${links.addAnEmployee.dtm.eventAsset}_${leftRailEventKey}`
  };

  const addAnEmployeeIconDtm = get(links, 'addAnEmployeeIcon.dtm') && {
    ...links.addAnEmployeeIcon.dtm,
    eventAsset: `${links.addAnEmployeeIcon.dtm.eventAsset}_${leftRailEventKey}`
  };

  return (
    <div className={leftRailStyles.addEmpCtaWrap}>
      <LazyScrollMetricTracker scrollDtm={cardInfo.addAnEmployeeScrollDtm}>
        <Link
          href={get(links, 'addAnEmployee.url') || ''}
          title={cardInfo.addEmpCardHoverText}
          onClick={() => {
            createDtmAndTrackActionHandler({
              dtmObj: addAnEmployeeDtm,
              actionTracking: get(links, 'addAnEmployee.actionTracking'),
              appendToActionAttr: `_${leftRailEventKey}`
            })();
          }}
        >
          <Sanitizer
            Component="span"
            safelySetInnerHTML={get(links, 'addAnEmployee.text') || ''}
          />
        </Link>
      </LazyScrollMetricTracker>
      {cardInfo.isGCSMember && (
        <span
          className={clsx(addEmpStyles.addEmployeeInfo, tooltipStyles.pdpIcon)}
          onClick={e => {
            e.stopPropagation();
            toggleModal();
            sendDTMClickEvent(addAnEmployeeIconDtm);
          }}
        >
          <Icon directory="dls" imageName="dls-icon-info-tooltip" $size={Size.XS} />
        </span>
      )}
    </div>
  );
};

const buildLockIcon = (links, colorVal) => {
  return {
    directory: 'dls',
    imageName: get(links, 'apply.imageName'),
    theme: {
      color: colorVal,
      marginRight: 5
    }
  };
};

// ====== public methods ======
/* eslint-disable complexity */
export const buildCtaList = ({
  cardInfo,
  links,
  labels,
  isLeftRail = false,
  isG171
}) => {
  const plainCtaTheme = {
    margin: 0,
    padding: 0,
    bgColor: Color.Gray01,
    textAlign: Align.Left
  };

  const { upgradeBtn, needUpgradeCta, cmPznEventInfo } = getUpgradeCtaProps(
    links,
    get(cardInfo, 'currentCard.pznOffer') || {},
    cardInfo.isGCSMember || cardInfo.isGCSLegacy
  );

  const ctaList = [];
  const additionalText = labels.upgradeCtaDescription || '';

  if (cardInfo.hasPznOffer) {
    if (needUpgradeCta) {
      const upgradeEventAsset = get(upgradeBtn, 'dtm.eventAsset') || '';
      upgradeBtn.dtm = {
        ...(upgradeBtn.dtm || {}),
        eventAsset: `${upgradeEventAsset}${leftRailEventKey}`
      };

      ctaList.push({
        Component: 'a',
        $type: Type.Primary,
        text: upgradeBtn.text,
        title: upgradeBtn.text,
        icon: buildLockIcon(links, Color.White),
        url: upgradeBtn.url,
        onClick: () => {
          sendDTMClickEvent(upgradeBtn.dtm);
        }
      });
    }

    const plainApplyDtm = get(links, 'applyOnline.dtm') || {};
    const linkDef = isLeftRail
      ? links.apply
      : {
          ...links.apply,
          dtm: {
            ...plainApplyDtm,
            eventAsset: `${plainApplyDtm.eventAsset}_Header`
          }
        };

    const applyClick = createCombinedApplyHandler({
      linkDef,
      card: cardInfo.currentCard,
      pagePznId: 'open_cd',
      cmPznEventInfo,
      modalName: `apply_${cardInfo?.currentCard?.shortName}`
    });
    ctaList.push({
      className: needUpgradeCta ? leftRailStyles.directLinkApplyCta : '',
      Component: 'button',
      $type: needUpgradeCta ? Type.Secondary : Type.Primary,
      text: get(links, 'apply.text') || '',
      title: get(links, 'apply.title') || '',
      icon: !isG171
        ? buildLockIcon(links, needUpgradeCta ? Color.BrightBlue : Color.White)
        : null,
      url: get(links, 'apply.url') || '',
      'aria-label': `Apply Now for ${cardInfo?.currentCard?.cardHeadline}`,
      onClick: applyClick,
      id: 'pdp-hero-apply-now'
    });

    if (needUpgradeCta) {
      ctaList.push({
        Component: 'span',
        className: leftRailStyles.additionalText,
        text: <Sanitizer safelySetInnerHTML={additionalText} />
      });
    }
  } else {
    ctaList.push({
      Component: 'div',
      theme: plainCtaTheme,
      text: <div className={leftRailStyles.applyPlaceholder} />
    });
  }

  if (cardInfo.isCardMemberCard) {
    const addEmpProps = {
      cardInfo,
      links
    };

    ctaList.push({
      Component: 'span',
      className: leftRailStyles.addEmpCtaOuterWrap,
      text: <AddEmployeeCta {...addEmpProps} />
    });
  }

  return ctaList;
};

export const buildDisclaimerList = (
  links,
  parent = '',
  needUpgradeCta,
  cardInfo
) => {
  const linkKeys = needUpgradeCta
    ? ['offerTermsUpgrade', 'ratesFeesUpgrade']
    : ['offerTerms', 'ratesFees'];
  return linkKeys.map((linkType = '') => {
    const dtmObj =
      (get(links, `${linkType}.dtm`) && {
        ...links[linkType].dtm,
        eventAsset: `${links[linkType].dtm.eventAsset}_${parent}`
      }) ||
      {};
    const text = links?.[linkType]?.text ?? '';
    return {
      url: get(links, `${linkType}.url`) || '',
      text: <Sanitizer safelySetInnerHTML={text} />,
      target: '_blank',
      'aria-label': `${textRemoveTags(text)} for ${textRemoveTags(
        cardInfo?.currentCard?.cardHeadline
      )}`,
      onClick: createDtmHandler({
        dtmObj
      }),
      qeId: 'sideRail-disclaimer-links'
    };
  });
};

export const formatLinksWithEep = ({ links, eep, appendEep, cardData = {} }) =>
  Object.entries(links).reduce((acc, [key, link = {}]) => {
    let eepToUse = eep;
    let allowUpgradeTo = false;
    const upgradeEepVal = getUpgradeEepForLink(link, cardData.pznOffer || {});

    if (upgradeEepVal) {
      eepToUse = upgradeEepVal;
      allowUpgradeTo = true;
    }

    const url = String(link.url || '').trim();
    if (url) {
      let formattedUrl;
      if (url.includes('${')) {
        formattedUrl = replaceStringWithVars({
          varsObject: {
            eep: eepToUse
          },
          string: url
        });
      } else if (appendEep && eepToUse) {
        const slashVal = url.endsWith('/') ? '' : '/';
        formattedUrl = `${url}${slashVal}${eepToUse}/`;
      } else {
        formattedUrl = url;
      }
      const dtmObj = link.dtm;
      acc[key] = {
        ...link,
        url: dtmObj ? mapUrlWithDtmLinkQuery(formattedUrl, dtmObj) : formattedUrl
      };
      if (allowUpgradeTo) {
        acc[key].allowUpgradeTo = true;
      }
    } else {
      acc[key] = link;
    }
    return acc;
  }, {});
